/**
 * Public Trade Associations Directory — /associations
 *
 * SEO/growth-facing page. NO auth. Visitors see every union we know
 * of, grouped by industry, with a click-through to the org profile
 * where they can claim their digital membership card.
 *
 * Ordering:
 *   • Default: geo-aware (region auto-detected from visitor IP)
 *   • Region chips let visitors pin to North / South / East / West
 *   • Falls back to "popular" (marquee first) if geo is unresolved.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import UnionLogo from "@/components/UnionLogo";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, Search, MapPin, ChevronRight, Sparkles, Users, Compass,
} from "lucide-react";

const INDUSTRY_LABELS = {
  bollywood:       "Bollywood",
  advertising:     "Advertising",
  broadcasting:    "Broadcasting & Media",
  events:          "Live Events & Entertainment",
  kollywood:       "Tamil (Kollywood)",
  tollywood:       "Telugu (Tollywood)",
  mollywood:       "Malayalam (Mollywood)",
  sandalwood:      "Kannada (Sandalwood)",
  marathi:         "Marathi Cinema",
  bengali:         "Bengali Cinema",
  gujarati:        "Gujarati Cinema",
  punjabi:         "Punjabi Cinema",
  bhojpuri:        "Bhojpuri Cinema",
  odia:            "Odia Cinema",
  assamese:        "Assamese Cinema",
  manipuri:        "Manipuri Cinema",
  sikkimese:       "Sikkimese Cinema",
  north_east:      "North-East Cinema",
  all_india:       "All-India Bodies",
  pan_india:       "Pan-India Bodies",
  general:         "General",
  performing_arts: "Performing Arts · Theatre",
};

const CATEGORY_LABELS = {
  labor_umbrella:  "Labor · Umbrella Federation",
  cine_workers:    "Labor · Cine Workers",
  direction_guild: "Direction Guild",
  directors:       "Direction Guild",
  producer_body:   "Producer Body",
  producer_apex:   "Producer Apex",
  dop_guild:       "Cinematographers",
  editor_guild:    "Editors",
  music_guild:     "Music · Sound",
  writer_guild:    "Writers",
  vfx_guild:       "VFX · Post",
  costume_guild:   "Costume · Make-up",
  makeup_hair:     "Make-up · Hair Stylists",
  stunt_guild:     "Stunts · Action",
  choreo_guild:    "Choreography · Dance",
  artiste_guild:   "Artistes · Talent",
  artiste_apex:    "Artistes · Federation",
  copyright_body:  "Copyright · Royalties",
  chamber:              "Chamber · Federation",
  business_chamber:     "Business Chamber · Apex",
  distributors:         "Distributors · National",
  exporters:            "Exporters · Foreign Rights & Impex",
  equipment_studios:    "Equipment · Studios",
  still_photography:    "Still Photography",
  doc_short_guild:      "Documentary · Short-Film Guild",
  film_school:          "Film School · Academic Body",
  producers_regional:   "Producers · Regional Chamber",
  sound:                "Sound",
  setting_light:        "Setting & Light",
  art:                  "Art Direction",
  assistant_directors:  "Assistant Directors",
  production_managers:  "Production Managers",
  theatre_umbrella:     "Theatre · Umbrella",
  theatre_federation:   "Theatre · Regional Federation",
  govt_cultural_apex:   "Govt · Cultural Apex",
  govt_zonal_cultural:  "Govt · Zonal Cultural Centre",
  // Advertising industry categories
  ad_apex:              "Advertising · Apex Council",
  ad_agencies:          "Advertising · Agency Body",
  ad_regulator:         "Advertising · Self-Regulator",
  advertisers_body:     "Advertising · Advertiser (Brand) Body",
  ad_networking:        "Advertising · Networking & Awards",
  ad_film_makers:       "Advertising · Ad-Film Makers Guild",
  ad_film_producers:    "Advertising · Ad-Film Producers Guild",
  ad_outdoor:           "Advertising · Outdoor / OOH",
  ad_research:          "Advertising · Market Research",
  ad_direct:            "Advertising · Direct Marketing",
  // Broadcasting & Media categories
  broadcasters:         "Broadcasting · TV / OTT",
  digital_streamers:    "Broadcasting · Streaming Platforms",
  broadcast_research:   "Broadcasting · Audience Research",
  print_publishers:     "Broadcasting · Newspaper & Magazine Publishers",
  radio_broadcasters:   "Broadcasting · Radio Operators",
  // Live Events categories
  events_apex:          "Events · Apex Body",
  events_industry:      "Events · Regional Chapter",
};

const REGION_CHIPS = [
  { key: "auto",  label: "Near me"    },
  { key: "north", label: "North"      },
  { key: "south", label: "South"      },
  { key: "east",  label: "East"       },
  { key: "west",  label: "West"       },
  { key: "all",   label: "Pan-India"  },
];

const CACHE_KEY = "cs_geo_region_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

async function getVisitorRegion() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.bucket || "";
  } catch { /* ignore */ }
  try {
    const { data } = await api.get("/geo/detect", { timeout: 3500 });
    const bucket = data.region_bucket || "";
    localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), bucket }));
    return bucket;
  } catch { return ""; }
}

export default function AssociationsDirectory() {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [q, setQ]               = useState("");
  const [industry, setIndustry] = useState("all");
  const [city, setCity]         = useState("all");
  const [region, setRegion]     = useState("auto");
  const [autoRegion, setAutoRegion] = useState("");
  const [facets, setFacets]     = useState({ industries: [], cities: [] });
  const [loading, setLoading]   = useState(true);

  // Detect visitor's macro-region once on mount. Runs in parallel to
  // the initial associations fetch so nothing blocks first paint.
  useEffect(() => {
    getVisitorRegion().then(setAutoRegion);
  }, []);

  useEffect(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (industry !== "all") params.industry = industry;
    if (city !== "all") params.city = city;

    // Resolve "auto" → visitor's detected region. If detection hasn't
    // returned yet (or resolved to empty), fall back to popular sort.
    const effectiveRegion = region === "auto" ? autoRegion : (region === "all" ? "" : region);
    if (effectiveRegion) params.region = effectiveRegion;
    // Explicit sort: popular when no region resolved so the marquee
    // names surface first.
    if (!effectiveRegion) params.sort = "popular";

    setLoading(true);
    api.get("/public/associations", { params })
      .then(({ data }) => {
        setRows(data.results);
        setTotal(data.total);
        setFacets(data.facets);
      })
      .finally(() => setLoading(false));
  }, [q, industry, city, region, autoRegion]);

  // Group results by industry → category (server order preserved via
  // `orderIdx` — Object.keys keeps insertion order in modern JS).
  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const ind = r.industry || "other";
      const cat = r.category || "other";
      g[ind] = g[ind] || {};
      g[ind][cat] = g[ind][cat] || [];
      g[ind][cat].push(r);
    }
    return Object.entries(g);
  }, [rows]);

  const nearMeLabel = autoRegion
    ? `Near me · ${autoRegion[0].toUpperCase()}${autoRegion.slice(1)} India`
    : "Near me";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="associations-directory">
      {/* Hero */}
      <div className="border-b border-white/10 bg-gradient-to-b from-[#141414] to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#F5B82E] flex items-center gap-2">
            <ShieldCheck size={11} /> Verified · Industry Union Registry
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]"
              data-testid="associations-hero-title">
            Every union<br />under one roof
          </h1>
          <p className="mt-5 text-white/60 max-w-2xl text-sm sm:text-base leading-relaxed">
            <b className="text-white/90">{total}</b> trade organisations recognised across
            Bollywood, regional cinema, TV, OTT and theatre.
            If your union is here, you can claim your digital membership card in under
            a minute — no more losing paper cards on set.
          </p>

          {/* Region chips — user selectable + auto-detected */}
          <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="associations-region-chips">
            <Compass size={13} className="text-[#F5B82E] mr-1" />
            {REGION_CHIPS.map(chip => (
              <button
                key={chip.key}
                onClick={() => setRegion(chip.key)}
                data-testid={`region-chip-${chip.key}`}
                className={
                  "h-8 px-3.5 rounded-full text-[10px] uppercase tracking-[0.24em] transition-colors " +
                  (region === chip.key
                    ? "bg-[#F5B82E] text-black font-semibold"
                    : "border border-white/15 text-white/65 hover:text-white hover:border-white/40")
                }
              >
                {chip.key === "auto" ? nearMeLabel : chip.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-10 border-y border-white/10 bg-[#0a0a0a]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search FWICE, IFTPC, WICA, IPRS, MSAA…"
              className="pl-9 bg-black/40 border-white/10 focus:border-[#F5B82E] h-10 text-sm"
              data-testid="associations-search" />
          </div>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="h-10 bg-black/40 border-white/10 w-[200px] text-sm" data-testid="associations-industry-filter">
              <SelectValue placeholder="All industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All industries</SelectItem>
              {facets.industries.map(i => (
                <SelectItem key={i} value={i}>{INDUSTRY_LABELS[i] || i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="h-10 bg-black/40 border-white/10 w-[180px] text-sm" data-testid="associations-city-filter">
              <SelectValue placeholder="All cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {facets.cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45" data-testid="associations-count">
            {rows.length} of {total}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading && (
          <div className="text-center py-16 text-white/40 uppercase tracking-[0.3em] text-xs">
            Loading unions…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-center py-16" data-testid="associations-empty">
            <div className="text-sm text-white/60">No unions match those filters.</div>
            <button onClick={() => { setQ(""); setIndustry("all"); setCity("all"); setRegion("all"); }}
              className="mt-3 text-[10px] uppercase tracking-[0.28em] text-[#F5B82E] hover:text-[#F5D95E]">
              Clear filters
            </button>
          </div>
        )}

        {!loading && grouped.map(([ind, catMap]) => (
          <section key={ind} className="mb-10" data-testid={`associations-section-${ind}`}>
            <h2 className="text-[10px] uppercase tracking-[0.32em] text-white/50 mb-4">
              {INDUSTRY_LABELS[ind] || ind}
              <span className="ml-2 text-white/30">
                {Object.values(catMap).reduce((n, list) => n + list.length, 0)}
              </span>
            </h2>
            {Object.entries(catMap).map(([cat, list]) => (
              <div key={cat} className="mb-6">
                <div className="text-[9px] uppercase tracking-[0.28em] text-white/40 mb-2">
                  {CATEGORY_LABELS[cat] || cat}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map(a => <AssociationCard key={a.id} a={a} />)}
                </div>
              </div>
            ))}
          </section>
        ))}
      </div>

      {/* Bottom nudge */}
      <div className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <Sparkles size={16} className="mx-auto text-[#F5B82E]" />
          <h3 className="mt-3 font-display text-2xl sm:text-3xl uppercase tracking-tight">
            Your union not listed?
          </h3>
          <p className="mt-3 text-sm text-white/55 max-w-lg mx-auto">
            We add unions on request within 48 hours. Message your union&apos;s secretary or
            send us their registration certificate — we&apos;ll seed them on the platform for free.
          </p>
          <Link to="/register"
            className="mt-6 inline-block px-6 py-3 rounded-full bg-[#F5B82E] hover:bg-[#F5D95E] text-black text-[11px] uppercase tracking-[0.24em] font-semibold"
            data-testid="associations-suggest">
            Suggest my union
          </Link>
        </div>
      </div>
    </div>
  );
}

function AssociationCard({ a }) {
  const kMembers = a.member_count >= 1000
    ? `${(a.member_count / 1000).toFixed(1)}k`
    : (a.member_count || null);
  return (
    <Link to={`/associations/${a.id}`}
      className="group rounded-xl p-4 border border-white/8 bg-black/30 hover:border-[#F5B82E]/60 hover:bg-black/50 transition-colors block relative overflow-hidden"
      data-testid={`association-card-${a.id}`}
      style={{ borderLeftColor: a.color, borderLeftWidth: 3 }}>
      <div className="flex items-start gap-3">
        <UnionLogo assoc={a} size={40} rounded="md" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg uppercase tracking-tight truncate" style={{ color: a.color }}>
            {a.name}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 truncate mt-0.5">
            {a.full}
          </div>
          <div className="mt-2 flex items-center gap-3 text-[10px] text-white/40">
            {a.city && <span className="inline-flex items-center gap-1"><MapPin size={9}/> {a.city}</span>}
            {kMembers && <span className="inline-flex items-center gap-1"><Users size={9}/> {kMembers}</span>}
          </div>
        </div>
        <ChevronRight size={14} className="text-white/25 group-hover:text-[#F5B82E] shrink-0 mt-1 transition-colors" />
      </div>
      {/* Hover CTA — appears in-place, doesn't shift layout */}
      <div className="mt-3 text-[9px] uppercase tracking-[0.26em] text-white/0 group-hover:text-[#F5B82E] transition-colors"
           data-testid={`association-claim-hint-${a.id}`}>
        Claim your card in 60 seconds →
      </div>
    </Link>
  );
}
